import { node } from 'prop-types'
import type { CSSProperties } from 'react'
import { Header } from 'semantic-ui-react'

function AboutFIndex() {
  const paragraphStyle = {
    marginBottom: '1em',
    textAlign: 'justify',
  } satisfies CSSProperties

  return (
    <div className='fade-in'>
      <Header as='h1' style={{ textAlign: 'center' }}>
        About F10.7 and F30 indices
      </Header>
      <div style={paragraphStyle}>
        Active and eruptive solar phenomena, for example solar flares or coronal mass ejections, comprise numerous
        interrelated physical processes of various spatial and time scales, which collectively cause substantial
        variations in the state of the solar atmosphere, interplanetary plasma and, indirectly, the Earth’s
        magnetosphere and ionosphere. The properties of the involved physical processes, their energy budgets, and their
        direct and indirect influences on the cosmic environment can be recognized, scrutinized and investigated only by
        means of remote recording of emitted radiation and/or by in&nbsp;situ measurements of plasma and field
        parameters.
      </div>

      <div style={paragraphStyle}>
        One of the most widely and long-used indicators of the solar activity level, and an indicator of individual
        active phenomena ongoing in the lower part of the solar atmosphere, is the F10.7 solar index, being the
        momentary (or averaged) flux of the solar radio emission integrated over the whole disk and over the
        100&nbsp;MHz-wide frequency band centered on 2800&nbsp;MHz (equivalently, 10.7&nbsp;cm wavelength). The
        10.7&nbsp;cm radio emission of the Sun can be recorded using relatively small, easy-to-maintain and inexpensive
        dish antennas supplied with low-noise amplifiers, digital receivers and automatic steering systems. Thanks to
        the excellent transmissivity of these radio waves through the Earth’s atmosphere, observations in the
        10.7&nbsp;cm band are virtually undisturbed by weather conditions. Regular observations of solar emission in the
        10.7&nbsp;cm waveband started in 1947 in Ottawa, Canada. In contemporary research, increasing emphasis is placed
        on multi-frequency observations. Consequently, measurements in alternative frequency bands are gaining
        significance, particularly the 1&nbsp;GHz band (30&nbsp;cm), which forms the basis for determining the F30 solar
        index.
      </div>

      <div style={paragraphStyle}>
        Basic physical mechanisms and statistical properties of solar radio emission in the microwave domain have been
        studied intensively. The emission is mostly thermal in origin and dominated by free-free processes in plasma
        (thermal bremsstrahlung). It is emitted by relatively cool plasma, including the quiet Sun’s chromosphere, but
        also by individual structures like coronal loops. However, the emission also has a gyroresonance component,
        emitted at low harmonics of the electron gyrofrequency by plasma volumes confined in strong magnetic fields over
        sunspots in active regions. Thus, the 10.7&nbsp;cm and 30&nbsp;cm radio emissions are produced mostly by plasma
        with properties typical of the lower corona and the upper chromosphere, reflecting processes ongoing in layers
        crucial for all kinds of solar active phenomena.
      </div>

      <div style={paragraphStyle}>
        Variations in time of the solar microwave emission can be subdivided into three main components: (I) a fast
        component varying on time scales of seconds to minutes, taking the form of easily distinguishable radio bursts;
        (II) a slowly varying component alternating on time scales from hours to years, related to the compound emission
        of active regions present on the Sun at a given phase of the activity cycle; and (III) the background emission
        of the quiet Sun. The variations of the slowly varying component related to the solar cycle (II) are well
        reflected by changes in the F10.7&nbsp;index. Over the eleven-year solar cycle the solar emission in the
        10.7&nbsp;cm band fluctuates roughly between 50&nbsp;SFU (5×10<sup>−21</sup>&nbsp;W·m<sup>−2</sup>·Hz
        <sup>−1</sup>) and 300&nbsp;SFU (3×10<sup>−20</sup>&nbsp;W·m<sup>−2</sup>·Hz<sup>−1</sup>). The medium- and
        long-term variations of the F30 index relatively closely follow those of F10.7. However, subtle differences in
        short-term variability are observed between the two, which can be attributed to distinct emission mechanisms and
        differences in the spatial distribution of their solar source regions.
      </div>

      <div style={paragraphStyle}>
        Long and basically homogeneous observational series of the F10.7 solar index are commonly applied in numerous
        statistical and correlative investigations of various aspects of solar activity and related space-weather
        phenomena. Variations of the F10.7 index and simultaneous variations of various solar emissions and indicators,
        such as soft and hard X-ray fluxes, sunspot numbers, mean solar magnetic fields, UV integrated fluxes, total
        solar irradiance, and full-disk Ca&nbsp;II and Mg&nbsp;II fluxes, are highly correlated. For this reason, the
        10.7&nbsp;cm integrated solar flux is widely recognized as an easy-to-record supplement or, to some extent, even
        a replacement for more difficult-to-record proxies of solar activity. The very high diagnostic potential of the
        F10.7 index is also broadly exploited in forecasting solar activity, predicting space-weather phenomena and even
        selected geophysical processes. For example, the 10.7&nbsp;cm solar flux represents combined emission of the
        chromosphere, transition region and corona modulated by solar active regions, which is deposited in the Earth’s
        thermosphere. The 10.7&nbsp;cm flux can also be applied in global MHD simulations of the Earth’s ionosphere as a
        surrogate for measurements in wavebands that cause photoionization and determine ionospheric ionization and
        conductivity. Similar studies can be conducted using measurements of the F30 index, with the aforementioned
        subtle differences between the two indices potentially enabling a more detailed investigation of various aspects
        of solar phenomena.
      </div>
    </div>
  )
}

AboutFIndex.propTypes = {
  children: node,
}

export default AboutFIndex
